/**
 * Der Sync-Vertrag zwischen App und Server.
 *
 * **Die tragende Beobachtung:** Fast alles, was offline entsteht, sind
 * *Ereignisse* — eine quittierte Einnahme, ein geloggter Satz, eine gegessene
 * Mahlzeit. Ereignisse kollidieren nicht: zwei Geräte erzeugen zwei verschiedene
 * Zeilen, keine Konkurrenz um dieselbe. Echte Konflikte gibt es nur bei
 * bearbeitbaren Objekten (dasselbe Supplement auf zwei Geräten umbenannt) — selten
 * genug, dass „die neuere Änderung gewinnt" die richtige Antwort ist.
 *
 * Deshalb reicht hier ein einfaches Verfahren statt eines Sync-Motors: Outbox,
 * Keyset-Cursor, LWW.
 */

/**
 * Tabellen, die synchronisiert werden. Wächst mit jeder Ausbaustufe.
 *
 * Die Reihenfolge ist auch die des Holens — und dabei nicht beliebig: Eine
 * Übung muss da sein, bevor ein Plan-Eintrag oder ein Satz auf sie zeigt.
 */
export const SYNC_TABLES = [
  'profiles',
  'supplements',
  'supplement_intakes',
  'reminders',
  'exercises',
  'training_plans',
  'plan_days',
  'plan_exercises',
  'workout_sessions',
  'workout_sets',
  'foods',
  'recipes',
  'recipe_ingredients',
  'recipe_cook_notes',
  'meal_plan_entries',
  'food_log',
  // Persönliche Abweichungen vom Haushaltsplan (27.10.2026) — nach dem Plan
  // geholt wäre schöner, aber der Verweis wird beim Server geprüft, nicht beim Holen.
  'meal_deviations',
  // Eine Mahlzeit an einem Tag auslassen (16.10.2026).
  'meal_skips',
  'water_log',
  'prep_checks',
  'training_slots',
  'training_days',
  'events',
  'feed_events',
  'event_marks',
  'journal_entries',
  'journal_notes',
  'journal_photos',
  'households',
  'household_members',
  'shops',
  // Orte des Haushalts (Standort, 27.10.2026).
  'places',
  'shopping_items',
  // Haushalts-Essensplan (27.10.2026): der Plan vor den Mitess-Zeilen, die auf ihn zeigen.
  'household_meals',
  'household_meal_eaters',
  'bucket_items',
  'pantry_items',
  'household_events',
  'task_lists',
  'tasks',
  'task_completions',
  // Nach den Mitgliedschaften: Wer was sehen darf, hängt an der Rolle (E16).
  'member_notes',
  // Standort (27.10.2026): wer was sieht, hängt an der Rolle.
  'location_checkins',
  // Akte (27.10.2026): nach den Mitgliedschaften — das Kind sieht seine nur mit Freigabe.
  'record_entries',
  // Taschengeld (E20) nach den Mitgliedschaften — wer es sieht, hängt an der Rolle.
  // Sparziele vor den Buchungen, die auf sie zeigen.
  'pocket_goals',
  'pocket_money',
  // Sterne (26.10.2026): wie das Taschengeld, ein eigenes Konto ohne Geld.
  'star_entries',
  // Kosten (E17): Kategorien und Regeln vor den Ausgaben, Ziele vor Einzahlungen.
  'expense_categories',
  'recurring_expenses',
  'expenses',
  'savings_goals',
  'savings_deposits',
] as const;

export type SyncTable = (typeof SYNC_TABLES)[number];

/**
 * Kosten (E17) sind Sache der Erwachsenen (23.10.2026): Ein Kind bekommt sie
 * nicht über den Abgleich, schreibt keine und zahlt nicht („bezahlt von“).
 * Die App blendet den Reiter aus — die Grenze zieht aber der Server.
 */
export const COST_TABLES = [
  'expense_categories',
  'recurring_expenses',
  'expenses',
  'savings_goals',
  'savings_deposits',
] as const satisfies readonly SyncTable[];

export function isCostTable(table: SyncTable): boolean {
  return (COST_TABLES as readonly SyncTable[]).includes(table);
}

/**
 * Ernährung (27.10.2026): Ein Kind schreibt hier nur, wenn es Stufe `teen`
 * hat **und** die Eltern Ernährung freigegeben haben (`childMayUseNutrition`).
 * Sonst weist der Push ab — auch wenn sein Gerät etwas anderes meint. Holen
 * darf es seine eigenen Zeilen weiter: Entzogen heißt verborgen, nicht
 * gelöscht.
 */
export const CHILD_NUTRITION_TABLES = [
  'foods',
  'recipes',
  'recipe_ingredients',
  'recipe_cook_notes',
  'meal_plan_entries',
  'food_log',
  'meal_skips',
  'water_log',
  'prep_checks',
] as const satisfies readonly SyncTable[];

export function isChildNutritionTable(table: SyncTable): boolean {
  return (CHILD_NUTRITION_TABLES as readonly SyncTable[]).includes(table);
}

/**
 * Training (28.10.2026): wie Ernährung — ein Kind schreibt hier nur als
 * `teen` mit Freigabe der Eltern (`childMayUseTraining`). Die Pause zum
 * Pausenende (`/rest-timer`) geht nicht über den Abgleich und prüft dieselbe
 * Regel eigens. Übungsgewichte im Satz sind keine Körperzahlen; das
 * Körpergewicht an Einheit und Satz schon (`CHILD_BODY_FIELDS`).
 */
export const CHILD_TRAINING_TABLES = [
  'exercises',
  'training_plans',
  'plan_days',
  'plan_exercises',
  'workout_sessions',
  'workout_sets',
  'training_slots',
  'training_days',
] as const satisfies readonly SyncTable[];

export function isChildTrainingTable(table: SyncTable): boolean {
  return (CHILD_TRAINING_TABLES as readonly SyncTable[]).includes(table);
}

/**
 * Supplements schreibt kein Kind, auch kein freigegebener Teen: Die Dosen im
 * Fachkonzept gelten für Erwachsene.
 */
export const CHILD_NEVER_TABLES = [
  'supplements',
  'supplement_intakes',
] as const satisfies readonly SyncTable[];

export function isChildNeverTable(table: SyncTable): boolean {
  return (CHILD_NEVER_TABLES as readonly SyncTable[]).includes(table);
}

/**
 * Körperzahlen, die ein Kind nie beim Server ablegt — auch nicht mit
 * Ernährung: Gewicht, Größe und alles, woraus die Energie-Schätzung rechnet.
 * Der Push leert sie, statt die Zeile abzuweisen (das Profil trägt auch die
 * Ziele, die ein Teen setzen darf).
 */
export const CHILD_BODY_FIELDS = {
  profiles: [
    'heightCm',
    'weightKg',
    'birthYear',
    'sexFormula',
    'activityLevel',
    'goal',
    'goalRatePct',
  ],
  journal_entries: ['bodyWeightKg', 'measurements'],
  // Training für ein Teen (28.10.2026): das Körpergewicht für Klimmzüge und
  // Co. — die Hantel im Satz (`weightKg`) bleibt.
  workout_sessions: ['bodyWeightKg'],
  workout_sets: ['bodyWeightKg'],
} as const satisfies Partial<Record<SyncTable, readonly string[]>>;

/**
 * Richtung je Tabelle.
 *
 * `pull` heißt: **der Server besitzt diese Zeilen.** Erinnerungen entstehen im
 * Scheduler; ein Client, der sie schreiben dürfte, könnte sich selbst
 * Benachrichtigungen unterschieben oder fremde stillstellen. Die Quittierung
 * läuft deshalb über `supplement_intakes`, nicht über `reminders`.
 */
export const SYNC_DIRECTIONS: Record<SyncTable, 'both' | 'pull'> = {
  profiles: 'both',
  supplements: 'both',
  supplement_intakes: 'both',
  reminders: 'pull',
  // Training entsteht offline im Gym — dort ist Schreiben der Normalfall,
  // nicht die Ausnahme.
  exercises: 'both',
  training_plans: 'both',
  plan_days: 'both',
  plan_exercises: 'both',
  workout_sessions: 'both',
  workout_sets: 'both',
  foods: 'both',
  recipes: 'both',
  recipe_ingredients: 'both',
  recipe_cook_notes: 'both',
  meal_plan_entries: 'both',
  food_log: 'both',
  meal_deviations: 'both',
  // Wie Einnahmen: Der Scheduler legt an, die App quittiert.
  meal_skips: 'both',
  water_log: 'both',
  prep_checks: 'both',
  // Trainingstage (E12): Die App plant, der Scheduler legt Termine zur
  // Erinnerung an — mit fester Kennung (`occurrenceId`), damit es dieselbe Zeile ist.
  training_slots: 'both',
  training_days: 'both',
  // Kalender (E12). Eingelesene Termine schreibt nur der Server beim Holen;
  // markiert wird über `event_marks`.
  events: 'both',
  feed_events: 'pull',
  event_marks: 'both',
  journal_entries: 'both',
  // Gedanken (E14): privat, nie im Haushalt.
  journal_notes: 'both',
  // Nur die Beschreibung des Fotos; das Bild geht über `PUT /photos/:id`.
  journal_photos: 'both',
  // Mitgliedschaft entsteht nur über Einladungscode und Beitritt (`/households`).
  // Dürfte der Client sie schreiben, könnte er sich in fremde Haushalte eintragen.
  households: 'pull',
  household_members: 'pull',
  shops: 'both',
  places: 'both',
  bucket_items: 'both',
  shopping_items: 'both',
  household_meals: 'both',
  household_meal_eaters: 'both',
  pantry_items: 'both',
  household_events: 'both',
  task_lists: 'both',
  tasks: 'both',
  task_completions: 'both',
  member_notes: 'both',
  location_checkins: 'both',
  record_entries: 'both',
  pocket_goals: 'both',
  pocket_money: 'both',
  star_entries: 'both',
  expense_categories: 'both',
  recurring_expenses: 'both',
  expenses: 'both',
  savings_goals: 'both',
  savings_deposits: 'both',
};

/**
 * Tabellen, die einem **Haushalt** gehören statt einem Konto.
 *
 * Hier ist die Grenze nicht `user_id`, sondern die Mitgliedschaft: Was auf der
 * Einkaufsliste steht, sehen und ändern alle Mitglieder desselben Haushalts —
 * und niemand sonst. `user_id` bleibt trotzdem an jeder Zeile und nennt, wer
 * sie angelegt hat.
 */
export const HOUSEHOLD_TABLES = [
  'households',
  'household_members',
  'shops',
  'places',
  'shopping_items',
  'pantry_items',
  'household_events',
  // Haushalts-Essensplan (27.10.2026): Plan und Wünsche, „wer isst mit“.
  'household_meals',
  'household_meal_eaters',
] as const satisfies readonly SyncTable[];

export type HouseholdTable = (typeof HOUSEHOLD_TABLES)[number];

export function isHouseholdTable(table: string): table is HouseholdTable {
  return (HOUSEHOLD_TABLES as readonly string[]).includes(table);
}

/**
 * Tabellen, deren Zeilen **privat oder geteilt** sein können (E12: Aufgaben).
 *
 * `household_id` leer: Die Zeile gehört dem Konto (`user_id`), wie jede
 * Konto-Tabelle. `household_id` gesetzt: Sie gehört dem Haushalt, wie die
 * Einkaufsliste. Eine Liste lässt sich so teilen, ohne umzuziehen. Jede
 * Prüfung — Holen, Schreiben, Verweise — fragt beide Fälle in **einer**
 * Bedingung ab (`apps/api/src/routes/sync/index.ts`, `visibleTo`).
 */
export const SHAREABLE_TABLES = [
  'task_lists',
  'tasks',
  'task_completions',
  // Kosten (E17): privat oder im Haushalt — dieselbe Abgrenzung.
  'expense_categories',
  'recurring_expenses',
  'expenses',
  'savings_goals',
  'savings_deposits',
  // Bucketliste (18.10.2026): privat ist die geheime Liste.
  'bucket_items',
] as const satisfies readonly SyncTable[];

export type ShareableTable = (typeof SHAREABLE_TABLES)[number];

export function isShareableTable(table: string): table is ShareableTable {
  return (SHAREABLE_TABLES as readonly string[]).includes(table);
}

export function isPushableTable(table: string): table is SyncTable {
  return (
    (SYNC_TABLES as readonly string[]).includes(table) &&
    SYNC_DIRECTIONS[table as SyncTable] === 'both'
  );
}

export function isSyncTable(table: string): table is SyncTable {
  return (SYNC_TABLES as readonly string[]).includes(table);
}

/** Felder, die jede synchronisierbare Zeile haben muss. */
export interface SyncedRow {
  id: string;
  updatedAt: string;
  deletedAt: string | null;
  [field: string]: unknown;
}

// --- Cursor ------------------------------------------------------------------

/**
 * Stand des Abgleichs je Tabelle: `<updatedAt ISO>|<id>`.
 *
 * Der Zeitstempel allein reicht **nicht**: zwei Zeilen können auf dieselbe
 * Millisekunde fallen, und ein Cursor rein auf Zeit würde bei genau diesem Paar
 * eine Zeile überspringen oder ewig wiederholen. Die Kennung als zweites
 * Sortierkriterium macht die Reihenfolge eindeutig.
 */
export type SyncCursor = string;

export const EMPTY_CURSOR: SyncCursor =
  '1970-01-01T00:00:00.000Z|00000000-0000-4000-8000-000000000000';

export function makeCursor(updatedAt: Date | string, id: string): SyncCursor {
  const iso = typeof updatedAt === 'string' ? updatedAt : updatedAt.toISOString();
  return `${iso}|${id}`;
}

export function parseCursor(cursor: string | undefined): { updatedAt: Date; id: string } {
  const raw = cursor && cursor.includes('|') ? cursor : EMPTY_CURSOR;
  const separator = raw.lastIndexOf('|');
  const timestamp = Date.parse(raw.slice(0, separator));
  const id = raw.slice(separator + 1);

  if (Number.isNaN(timestamp)) {
    return parseCursor(EMPTY_CURSOR);
  }
  return { updatedAt: new Date(timestamp), id };
}

// --- Mutationen --------------------------------------------------------------

export type MutationKind = 'upsert' | 'delete';

/** Ein Eintrag der Outbox: eine lokal vollzogene Änderung, noch nicht beim Server. */
export interface Mutation<T extends SyncedRow = SyncedRow> {
  /**
   * Eigene Kennung der Änderung.
   *
   * Macht das Senden wiederholbar: geht die Antwort verloren und die App schickt
   * erneut, darf nichts doppelt passieren. Beim Anwenden ist das ohnehin gegeben —
   * dieselbe Änderung hat denselben `updatedAt` und verliert damit gegen sich
   * selbst (siehe `incomingWins`). Die Kennung dient der Nachvollziehbarkeit.
   */
  mutationId: string;
  table: SyncTable;
  kind: MutationKind;
  row: T;
}

export interface PushRequest {
  mutations: Mutation[];
}

export interface PushResponse {
  /** Übernommene Änderungen — die App räumt sie aus der Outbox. */
  applied: string[];
  /**
   * Abgelehnte Änderungen mit Grund.
   *
   * Wird in der Oberfläche sichtbar gemacht, nicht verschluckt: **stiller
   * Datenverlust ist das einzige wirklich schlimme Verhalten.**
   */
  rejected: { mutationId: string; reason: string }[];
}

export interface TableChanges {
  table: SyncTable;
  rows: SyncedRow[];
  cursor: SyncCursor;
}

export interface PullResponse {
  changes: TableChanges[];
  /**
   * Ende-zu-Ende (E21): Hat das Konto einen Kontoschlüssel eingerichtet? Dann
   * nimmt der Server für verschlüsselte Tabellen keinen Klartext mehr an, und
   * das Gerät hält solche Änderungen, bis es entsperrt ist.
   */
  encryption?: boolean;
  /** Haushalte mit Haushaltsschlüssel (E21) — dort geteilte Zeilen nur als Chiffrat. */
  encryptedHouseholds?: string[];
  /** Es liegt noch mehr bereit — die App holt direkt weiter. */
  hasMore: boolean;
  /**
   * Die Haushalte, in denen das Konto **jetzt** Mitglied ist.
   *
   * Der Cursor allein kann einen Wechsel nicht abbilden: Wer einem Haushalt
   * beitritt, hat dessen ältere Zeilen nie gesehen, und sie liegen zeitlich vor
   * seinem Cursor. Wer austritt, bekommt keine Löschung mehr geschickt — die
   * Zeilen sind für ihn ja nicht mehr sichtbar. Ändert sich diese Liste, setzt
   * die App die Haushalts-Cursor zurück und räumt fremd gewordene Zeilen weg.
   */
  householdIds: string[];
}

/** Wie viele Zeilen je Tabelle und Runde höchstens geholt werden. */
export const PULL_PAGE_SIZE = 200;
/** Wie viele Änderungen die App je Runde höchstens sendet. */
export const PUSH_BATCH_SIZE = 100;

// --- Konfliktregel -----------------------------------------------------------

/**
 * Gewinnt die eingehende Zeile gegen die vorhandene?
 *
 * Gleichstand geht an den Bestand — eine Wiederholung derselben Änderung darf
 * nichts umschreiben und keinen neuen Zeitstempel erzeugen.
 */
export function incomingWins(
  incoming: Pick<SyncedRow, 'updatedAt'>,
  existing: Pick<SyncedRow, 'updatedAt'> | undefined | null,
): boolean {
  if (!existing) return true;
  return Date.parse(incoming.updatedAt) > Date.parse(existing.updatedAt);
}

/**
 * Wie weit ein Client-Zeitstempel in der Zukunft liegen darf, bevor er auf die
 * Serverzeit gesetzt wird.
 *
 * Ohne diese Grenze könnte eine falsch gestellte Geräteuhr — oder ein Angreifer —
 * eine Zeile mit dem Jahr 2099 schreiben, die danach **jede** spätere Änderung
 * gewinnt. Der Datensatz wäre eingefroren, ohne dass jemand versteht warum.
 */
export const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

export function clampToServerTime(clientTime: string, now: Date): Date {
  const parsed = Date.parse(clientTime);
  if (Number.isNaN(parsed)) return now;
  if (parsed > now.getTime() + MAX_CLOCK_SKEW_MS) return now;
  return new Date(parsed);
}

/**
 * Notizen über ein Kind (E16): geschrieben wie eine Konto-Zeile (nur, wer sie
 * angelegt hat), gelesen nach `visibility` — nur ich, die Erwachsenen im
 * Haushalt, oder auch das Kind. Die vierte Abgrenzung neben Konto, Haushalt
 * und teilbar.
 */
export const MEMBER_NOTE_TABLES = ['member_notes'] as const satisfies readonly SyncTable[];

export function isMemberNoteTable(table: string): table is (typeof MEMBER_NOTE_TABLES)[number] {
  return (MEMBER_NOTE_TABLES as readonly string[]).includes(table);
}

/**
 * Taschengeld (E20): die fünfte Abgrenzung. Lesen das Kind und die
 * Erwachsenen seines Haushalts, geschrieben wird wie eine Konto-Zeile; was
 * wer schreiben darf, prüft der Server eigens (`checkPocket`).
 */
export const POCKET_TABLES = [
  'pocket_goals',
  'pocket_money',
  'star_entries',
] as const satisfies readonly SyncTable[];

export function isPocketTable(table: string): table is (typeof POCKET_TABLES)[number] {
  return (POCKET_TABLES as readonly string[]).includes(table);
}

/**
 * Standort-Check-ins (27.10.2026): die sechste Abgrenzung. Lesen der
 * Verfasser selbst und die **Erwachsenen** seines Haushalts — ein Kind sieht
 * nur die eigenen (`locationVisibleTo`). Geschrieben wird wie eine
 * Konto-Zeile; ein Kind nur, wenn die Eltern es eingeschaltet haben
 * (`household_members.location_sharing`, `checkCheckin`).
 */
export const LOCATION_TABLES = ['location_checkins'] as const satisfies readonly SyncTable[];

export function isLocationTable(table: string): table is (typeof LOCATION_TABLES)[number] {
  return (LOCATION_TABLES as readonly string[]).includes(table);
}

/**
 * Akte (27.10.2026): die siebte Abgrenzung. Die eigene Akte ist privat (ohne
 * Haushalt, nur ich — auch nicht der Partner); die Akte eines Kindes gehört
 * dem Haushalt: lesen und schreiben alle **Erwachsenen** dort, das Kind liest
 * nur, wenn `visibility = 'child'`, und schreibt nie (`recordVisibleTo`,
 * `checkRecord`).
 */
export const RECORD_TABLES = ['record_entries'] as const satisfies readonly SyncTable[];

export function isRecordTable(table: string): table is (typeof RECORD_TABLES)[number] {
  return (RECORD_TABLES as readonly string[]).includes(table);
}
